import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, RefreshCcw } from 'lucide-react';

type QuestionOption = {
  value: string;
  label: string;
};

type Question = {
  id: string;
  title: string;
  options: QuestionOption[];
  multiple?: boolean;
};

type Answers = {
  teamSize: string;
  projectTypes: string[];
  monthlyProjects: string;
  challenges: string[];
};

type Plan = {
  name: string;
  price: number;
  features: string[];
};

const DiscoveryTool: React.FC = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({
    teamSize: '',
    projectTypes: [],
    monthlyProjects: '',
    challenges: []
  });

  const questions: Question[] = [
    {
      id: 'teamSize',
      title: 'Quante persone lavorano nel tuo team di sviluppo?',
      options: [
        { value: 'solo', label: 'Sono solo io' },
        { value: 'small', label: '2-5 persone' },
        { value: 'medium', label: '6-10 persone' },
        { value: 'large', label: 'Più di 10 persone' }
      ]
    },
    {
      id: 'projectTypes',
      title: 'Che tipo di progetti sviluppate principalmente?',
      options: [
        { value: 'ecommerce', label: 'E-commerce' },
        { value: 'corporate', label: 'Siti Corporate' },
        { value: 'landing', label: 'Landing Pages' },
        { value: 'webapp', label: 'Web Applications' }
      ],
      multiple: true
    },
    {
      id: 'monthlyProjects',
      title: 'Quanti progetti gestite mensilmente?',
      options: [
        { value: 'few', label: '1-2 progetti' },
        { value: 'medium', label: '3-5 progetti' },
        { value: 'many', label: 'Più di 5 progetti' }
      ]
    },
    {
      id: 'challenges',
      title: 'Quali sono le sfide principali che state affrontando?',
      options: [
        { value: 'time', label: 'Tempi di consegna stretti' },
        { value: 'resources', label: 'Mancanza di risorse interne' },
        { value: 'quality', label: 'Mantenere alta la qualità' },
        { value: 'costs', label: 'Gestione dei costi del personale' },
        { value: 'scaling', label: 'Difficoltà a scalare' }
      ],
      multiple: true
    }
  ];

  const plans: Record<string, Plan> = {
    single: {
      name: 'Base',
      price: 2000,
      features: [
        '1 task alla volta',
        'Avvio entro 24h dalla richiesta',
        'Dashboard di gestione task',
        'Supporto via email (entro 24h)'
      ]
    },
    double: {
      name: 'Pro',
      price: 3500,
      features: [
        'Fino a 2 task in parallelo',
        'Avvio entro 24h dalla richiesta',
        'Dashboard di gestione task con priorità avanzata',
        'Supporto prioritario via email (entro 12h)',
        'Report settimanali con aggiornamenti sugli sviluppi'
      ]
    },
    multiple: {
      name: 'Enterprise',
      price: 5000,
      features: [
        'Fino a 3 task in parallelo',
        'Avvio entro 24h dalla richiesta',
        'Dashboard di gestione task con priorità massima',
        'Canale di comunicazione dedicato (Slack/WhatsApp)',
        'Risposte rapide (entro 6h)',
        'Report settimanali dettagliati',
        'Consulenza strategica mensile per ottimizzare il flusso di lavoro'
      ]
    }
  };

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers(prev => {
      const currentQuestion = questions.find(q => q.id === questionId);
      
      if (currentQuestion?.multiple) {
        const currentValues = prev[questionId as keyof Answers] as string[] || [];
        if (currentValues.includes(value)) {
          return {
            ...prev,
            [questionId]: currentValues.filter(v => v !== value)
          };
        }
        return {
          ...prev,
          [questionId]: [...currentValues, value]
        };
      }
      return {
        ...prev,
        [questionId]: value
      };
    });
  };

  const getRecommendation = (): Plan => {
    const { monthlyProjects, challenges = [], teamSize } = answers;
    
    if (
      monthlyProjects === 'many' || 
      (challenges && challenges.length >= 3) || 
      teamSize === 'large'
    ) {
      return plans.multiple;
    } else if (
      monthlyProjects === 'medium' || 
      (challenges && challenges.includes('scaling')) || 
      teamSize === 'medium'
    ) {
      return plans.double;
    }
    return plans.single;
  };

  const saveEntry = async (action: string, useBeacon: boolean = false) => {
    const entryData = {
      ...answers,
      action,
      timestamp: new Date().toISOString(),
      recommendedPlan: getRecommendation().name
    };

    // Converti l'oggetto in stringa JSON
    const jsonData = JSON.stringify(entryData);

    if (useBeacon && 'sendBeacon' in navigator) {
      // Usa sendBeacon per chiusura pagina
      const blob = new Blob([jsonData], { type: 'application/json' });
      const success = navigator.sendBeacon('/api/save-entry.php', blob);
      
      // Log per debug
      console.log('Beacon send attempt:', success);
      return success;
    }

    // Metodo standard per salvataggio
    try {
      const response = await fetch('/api/save-entry.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: jsonData
      });

      const result = await response.json();
      if (result.status !== 'success') {
        console.error('Salvataggio entry fallito');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Errore nel salvataggio entry:', error);
      return false;
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Salva solo se ci sono risposte parziali
      if (step > 0 && step < questions.length) {
        // Rimuovi l'event listener per evitare chiamate multiple
        window.removeEventListener('beforeunload', handleBeforeUnload);
        
        // Forza il salvataggio sincrono
        const jsonData = JSON.stringify({
          ...answers,
          action: 'exit',
          timestamp: new Date().toISOString(),
          recommendedPlan: getRecommendation().name
        });

        // Usa fetch sincrono come fallback
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/save-entry.php', false);  // Sincrono
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(jsonData);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Pulizia dell'event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [step, answers]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Salva solo se ci sono risposte parziali e la pagina è nascosta
      if (document.hidden && step > 0 && step < questions.length) {
        saveEntry('hidden', true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Pulizia dell'event listener
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [step, answers]);

  const currentQuestion = questions[step];
  const recommendation = getRecommendation();

  const renderQuestion = () => (
    <div className="w-full max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">{currentQuestion.title}</h2>
      <div className="grid gap-4">
        {currentQuestion.options.map((option) => (
          <button
            key={option.value}
            className={`w-full p-3 text-left rounded-lg transition-colors duration-200 ${
              (currentQuestion.multiple
                ? (answers[currentQuestion.id as keyof Answers] as string[]).includes(option.value)
                : answers[currentQuestion.id as keyof Answers] === option.value)
                ? "bg-black text-white"
                : "border border-black text-black hover:bg-gray-100"
            }`}
            onClick={() => handleAnswer(currentQuestion.id, option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="flex justify-between mt-6">
        <button
          onClick={() => setStep(prev => Math.max(0, prev - 1))}
          disabled={step === 0}
          className="px-4 py-2 border border-black text-black rounded-lg disabled:opacity-50"
        >
          Indietro
        </button>
        <button
          onClick={() => setStep(prev => prev + 1)}
          disabled={
            !answers[currentQuestion.id as keyof Answers] ||
            (currentQuestion.multiple && (answers[currentQuestion.id as keyof Answers] as string[]).length === 0)
          }
          className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50 flex items-center"
        >
          Avanti <ArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="w-full max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">Il Piano Perfetto per Te</h2>
      <div className="space-y-4">
        <div className="bg-black text-white p-4 rounded-lg">
          <h3 className="text-2xl font-bold mb-2">{recommendation.name}</h3>
          <p className="text-3xl font-extrabold">€{recommendation.price}/mese</p>
        </div>
        <div>
          <h4 className="text-lg font-semibold mb-2">Cosa include:</h4>
          <ul className="space-y-2">
            {recommendation.features.map((feature, index) => (
              <li key={index} className="flex items-center">
                <CheckCircle className="mr-2 h-5 w-5 text-black" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex space-x-4 mt-4">
          <button 
            className="w-full p-3 bg-black text-white rounded-lg flex items-center justify-center"
            onClick={() => {
              saveEntry('restart');
              setStep(0);
            }}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Ricomincia
          </button>
          <button 
            className="w-full p-3 border border-black text-black rounded-lg"
            onClick={() => saveEntry('contact')}
          >
            <a href="mailto:hi@etichettabianca.dev">Contattaci</a>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="text-center mb-8">
        <h1 className="text-5xl font-bold text-black mb-2 font-['Alfa_Slab_One']">Etichetta Bianca</h1>
        <p className="text-black text-lg">
          Il tuo team di sviluppo, senza compromessi
        </p>
      </div>
      <div className="w-full max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-md">
        {step < questions.length ? renderQuestion() : renderResults()}
      </div>
    </div>
  );
};

export default DiscoveryTool;
